import React, { useContext } from "react";

import type {
    FavoritesContentBlock,
    GridPromoPlacements,
    ServerCmsTypesCategoryNavItem,
} from "@reactivated";

import { ReactivatedContext } from "../../context";
import { FavoritesContext } from "../../favorites";
import { Themed } from "../../styles/themes";
import { SubcategoryTiles } from "../SubcategoryTiles";
import { FavoritesContent } from "./";
import { useFavoriteProducts } from "./useFavoriteProducts";

import styles from "./index.module.scss";

interface OwnedFavoritesContentProps {
    value: FavoritesContentBlock;
    subcategoryTiles: ServerCmsTypesCategoryNavItem[];
    productsPageTitle: string;
    gridPromos?: GridPromoPlacements;
}

export const OwnedFavoritesContent = ({
    value,
    subcategoryTiles,
    productsPageTitle,
    gridPromos,
}: OwnedFavoritesContentProps) => {
    const favs = useContext(FavoritesContext);
    const context = useContext(ReactivatedContext);
    const locale = context.i18n.language.code;

    const { products, isLoading, hasError } = useFavoriteProducts(
        favs.items,
        locale,
    );
    const skus = favs.items.map((item) => item.sku);

    if (favs.items.length === 0) {
        return (
            <Themed theme="offWhite">
                <div className={styles.zeroState}>
                    <p className={styles.zeroStateHeading}>
                        {value.zero_state_heading}
                    </p>
                </div>
                {subcategoryTiles.length > 0 && (
                    <>
                        <h2 className={styles.subcategoryHeading}>
                            {value.browse_heading}
                        </h2>
                        <SubcategoryTiles
                            tiles={subcategoryTiles}
                            categoryTitle={productsPageTitle}
                        />
                    </>
                )}
            </Themed>
        );
    }

    const sharedPage = value.shared_favorites_page;
    const shareUrl = sharedPage
        ? (() => {
              const url = new URL(sharedPage.url, window.location.origin);
              skus.forEach((sku) => url.searchParams.append("sku", sku));
              return url.toString();
          })()
        : null;

    return (
        <FavoritesContent
            products={products}
            isLoading={isLoading}
            hasError={hasError}
            skus={skus}
            shareUrl={shareUrl}
            errorMessage={value.error_message}
            footerContent={value.footer_content ?? null}
            gridPromos={gridPromos}
        />
    );
};
